import React, { useEffect, useState, useCallback } from "react";
import { MDBCol, MDBRow } from "mdbreact";
import { message, Form, Input, Select, Image, Skeleton } from "antd";
import { Button, Checkbox, Text } from "evergreen-ui";
import Firebase from "../../Firebase/Firebase";
import { useListVals, useObject } from "../../Firebase/databaseHooks";
import FireFetch from "../../Firebase/FireFetch";
import { getDatabase, ref, update } from "firebase/database";
import {
  getStorage,
  deleteObject,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  listAll,
} from "firebase/storage";
import { useParams, useLocation } from "react-router-dom";
import { useAuth } from "../../AuthContext/AuthContext";

const { TextArea } = Input;

const storage = getStorage(Firebase);
const db = getDatabase(Firebase);

const EditLessonModal = (props) => {
  const { id } = useParams();
  const { canAccessModule } = useAuth();
  const canUpdateLesson = canAccessModule("LESSONS", "update");
  const location = useLocation();
  const [form] = Form.useForm();

  const [coreChecked, setCoreChecked] = useState(false);
  const [filesList, setFilesList] = useState([]);
  const [showLoading, setShowLoading] = useState(false);
  const [lesson, setLesson] = useState(location?.state?.lesson);
  const [mediaLinks, setMediaLinks] = useState({});

  const [languagesArray, languagesLoading] = useListVals(ref(db, "talmidsdb/languages"));
  const [categoriesArray, categoriesLoading] = useListVals(ref(db, "talmidsdb/categories"));
  const [subCategories, subCategoriesLoading] = useListVals(ref(db, "talmidsdb/subCategories"));
  const [lessonOwners, lessonOwnersLoading] = useListVals(ref(db, "talmidsdb/lessonOwners"));
  const [usersArray, usersLoading] = useListVals(ref(db, "talmidsdb/users"));
  const [organisations, organisationsLoading] = useListVals(ref(db, "talmidsdb/organisations"));
  const [countryArray, countriesLoading] = useListVals(ref(db, "talmidsdb/countries")); // 🌍 NEW
  const [snapshot, loading, error] = useObject(ref(db, "talmidsdb/lessons/" + id));
  const [lessons, lessonsLoading] = useListVals(ref(db, "talmidsdb/lessons"));
  const [lessonTypes, lessonTypesLoading] = useListVals(ref(db, "talmidsdb/lessonTypes")); // 🌟 NEW
  const lookupLoading =
    loading ||
    languagesLoading ||
    categoriesLoading ||
    subCategoriesLoading ||
    lessonOwnersLoading ||
    usersLoading ||
    organisationsLoading ||
    countriesLoading ||
    lessonsLoading ||
    lessonTypesLoading;

  const loadMediaFiles = useCallback(async () => {
    try {
      const res = await listAll(storageRef(storage, "System/Lessons/" + id));
      const mediaPromises = res.items.map(async (itemRef) => {
        if (itemRef.name === "lessonImage") {
          const url = await getDownloadURL(itemRef);
          return { [itemRef.name]: url };
        }
        return {};
      });

      const mediaResults = await Promise.all(mediaPromises);
      setMediaLinks(Object.assign({}, ...mediaResults));
    } catch (error) {
      console.error("Error loading media files:", error);
    }
  }, [id]);

  useEffect(() => {
    if (!loading && snapshot) {
      const lessonData = snapshot.val();
      setLesson(lessonData);

      if (lessonData) {
        form.setFieldsValue({
          lessonName: lessonData?.lessonName || "",
          lessonDescription: lessonData?.lessonDescription || "",
          lessonCode: lessonData?.lessonCode || "",
          language: lessonData?.language || undefined,
          lessonLevel: lessonData?.lessonLevel || undefined,
          lessonDifficulty: lessonData?.lessonDifficulty || undefined,
          lessonStatus: lessonData?.lessonStatus || undefined,
          lessonTheme: lessonData?.lessonTheme || "",
          materialsNeeded: lessonData?.materialsNeeded || "",
          order: lessonData?.order || "",
          assignedTo: lessonData?.assignedTo || undefined,
          organisation: lessonData?.organisation || undefined,
          owner: lessonData?.owner || undefined,
          categories: lessonData?.categories || [],
          subCategories: lessonData?.subCategories || [],
          internalLinks: lessonData?.internalLinks || "",
          nextLesson: lessonData?.nextLesson || "",
          lessonType: lessonData?.lessonType || undefined, // 🌟 NEW
          countries: lessonData?.countries || [], // 🌍 NEW
        });

        setCoreChecked(lessonData?.coreLesson || false);
        loadMediaFiles();
      }
    }
  }, [snapshot, loading, form, id, loadMediaFiles]);

  const handleFileUpload = useCallback((e) => {
    if (e.target.files && e.target.files[0]) {
      setFilesList([
        {
          fileID: e.target.id,
          file: e.target.files[0],
        },
      ]);
    }
  }, []);

  const addFile = useCallback(
    async (fileObject) => {
      try {
        const fileRef = storageRef(
          storage,
          "System/Lessons/" + id + "/" + fileObject.fileID
        );
        await uploadBytes(fileRef, fileObject.file);
        return await getDownloadURL(fileRef);
      } catch (error) {
        console.error("Error uploading file:", error);
        throw error;
      }
    },
    [id]
  );

  const isEqual = useCallback((a, b) => {
    if (Array.isArray(a) && Array.isArray(b)) {
      return a.length === b.length && a.every((val, index) => val === b[index]);
    }
    return a === b;
  }, []);

  const handleEditLesson = useCallback(
    async (values) => {
      if (!canUpdateLesson) {
        message.error("You do not have permission to update lessons.");
        return;
      }

      setShowLoading(true);

      try {
        const updates = {};
        const formValues = form.getFieldsValue();

        Object.keys(formValues).forEach((key) => {
          if (!isEqual(formValues[key], lesson?.[key])) {
            updates[key] = formValues[key];
          }
        });

        if (coreChecked !== lesson?.coreLesson) {
          updates.coreLesson = coreChecked;
        }

        if (filesList.length > 0) {
          updates.lessonImage = await addFile(filesList[0]);
        }

        if (Object.keys(updates).length > 0) {
          await update(ref(db, "talmidsdb/lessons/" + id), updates);
          await FireFetch.addLog("lessons", id, "Edit");
          message.success("Lesson updated successfully");
        } else {
          message.info("No changes detected");
        }
      } catch (error) {
        console.error("Error updating lesson:", error);
        message.error("Unable to update lesson due to an error");
      } finally {
        setShowLoading(false);
        props.modal(false);
      }
    },
    [form, lesson, coreChecked, filesList, id, props, addFile, isEqual, canUpdateLesson]
  );

  const deleteMedia = useCallback(
    async (reference) => {
      if (!canUpdateLesson) {
        message.error("You do not have permission to update lessons.");
        return;
      }

      try {
        await deleteObject(
          storageRef(storage, "System/Lessons/" + id + "/" + reference)
        );
        message.success("Image removed successfully");

        setMediaLinks((prev) => {
          const updated = { ...prev };
          delete updated[reference];
          return updated;
        });

        await update(ref(db, "talmidsdb/lessons/" + id), { [reference]: null });
      } catch (error) {
        message.error("Unable to remove image, an error occurred");
      }
    },
    [id, canUpdateLesson]
  );

  if (lookupLoading) return <Skeleton active paragraph={{ rows: 14 }} />;
  if (error) return <div>Error loading lesson: {error.message}</div>;

  return (
    <Form layout="vertical" form={form} onFinish={handleEditLesson}>
      <MDBRow>
        <MDBCol md={12}>
          <div className="d-flex justify-content-between">
            <p className="h6 text-center py-4">Edit Lesson</p>
          </div>

          <Form.Item
            label="Lesson Name"
            name="lessonName"
            rules={[{ required: true }]}
          >
            <Input placeholder="Enter lesson name" />
          </Form.Item>

          <Form.Item label="Lesson Description" name="lessonDescription">
            <TextArea rows={3} placeholder="Enter lesson description" />
          </Form.Item>

          <Form.Item label="Lesson Code" name="lessonCode">
            <Input placeholder="Enter lesson code" />
          </Form.Item>

          <Form.Item label="Language" name="language">
            <Select placeholder="Select language">
              {languagesArray?.map((lang) => (
                <Select.Option key={lang.id} value={lang.id}>
                  {lang.languageName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {/* 🌍 NEW: Countries */}
          <Form.Item label="Countries" name="countries">
            <Select
              mode="multiple"
              placeholder="Select countries"
              optionFilterProp="children"
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {countryArray?.map((country) => (
                <Select.Option key={country.id} value={country.id}>
                  {country.countryName || country.name || "Unnamed country"}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {/* 🌟 NEW: Lesson Type */}
          <Form.Item label="Lesson Type" name="lessonType">
            <Select placeholder="Select lesson type">
              {lessonTypes?.map((lt) => (
                <Select.Option key={lt.id} value={lt.id}>
                  {lt.typeName || lt.lessonTypeName || "Untitled type"}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Categories" name="categories">
            <Select
              mode="multiple"
              placeholder="Select categories"
              optionFilterProp="children"
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {categoriesArray?.map((category) => (
                <Select.Option key={category.id} value={category.id}>
                  {category.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Sub Categories" name="subCategories">
            <Select
              mode="multiple"
              placeholder="Select sub categories"
              optionFilterProp="children"
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {subCategories?.map((subCat) => (
                <Select.Option key={subCat.id} value={subCat.id}>
                  {subCat.categoryName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Lesson Level" name="lessonLevel">
            <Select placeholder="Select lesson level">
              <Select.Option value="level-1">
                Level 1 - Introduction
              </Select.Option>
              <Select.Option value="level-2">
                Level 2 - Intermediate
              </Select.Option>
              <Select.Option value="level-3">
                Level 3 - Advanced
              </Select.Option>
            </Select>
          </Form.Item>

          <Form.Item label="Difficulty Level" name="lessonDifficulty">
            <Select placeholder="Select difficulty level">
              <Select.Option value="easy">Easy</Select.Option>
              <Select.Option value="intermediate">Intermediate</Select.Option>
              <Select.Option value="challenging">Challenging</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item label="Lesson Status" name="lessonStatus">
            <Select placeholder="Select lesson status">
              <Select.Option value="draft">Draft</Select.Option>
              <Select.Option value="archived">Archived</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item label="Lesson Theme" name="lessonTheme">
            <Input placeholder="Enter lesson theme" />
          </Form.Item>

          <Form.Item label="Materials Needed" name="materialsNeeded">
            <TextArea rows={2} placeholder="Enter materials needed" />
          </Form.Item>

          <Form.Item label="Order" name="order">
            <Input type="number" placeholder="Enter order number" />
          </Form.Item>

          <Form.Item label="Organisation" name="organisation">
            <Select placeholder="Select organisation">
              {organisations?.map((org) => (
                <Select.Option key={org.id} value={org.id}>
                  {org.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Assigned To" name="assignedTo">
            <Select placeholder="Assign To">
              {usersArray?.map((user) => (
                <Select.Option key={user.id} value={user.id}>
                  {`${user.firstname} ${user.surname}`}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Owner" name="owner">
            <Select placeholder="Select owner">
              {lessonOwners?.map((lessonOwner) => (
                <Select.Option key={lessonOwner.name} value={lessonOwner.name}>
                  {lessonOwner.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Lesson Image">
            <Input
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              id="lessonImage"
            />
            {mediaLinks["lessonImage"] && (
              <div className="">
                <Text size={300}>Current image:</Text>
                <div>
                  <Image
                    width={200}
                    src={mediaLinks["lessonImage"]}
                    alt="Lesson thumbnail"
                  />
                </div>
                <div>
                  <Button
                    onClick={() => {
                      if (
                        window.confirm(
                          "Are you sure you want to delete this image?"
                        )
                      ) {
                        deleteMedia("lessonImage");
                      }
                    }}
                    appearance="primary"
                    intent="danger"
                  >
                    Remove Image
                  </Button>
                </div>
              </div>
            )}
          </Form.Item>

          <Form.Item>
            <Text>Core Lesson</Text>
            <Checkbox
              checked={coreChecked}
              onChange={(e) => setCoreChecked(e.target.checked)}
            />
          </Form.Item>

          <Form.Item label="Next Lesson" name="nextLesson">
            <Select placeholder="Select Next Lesson">
              {lessons?.map((lessonItem) => (
                <Select.Option key={lessonItem.id} value={lessonItem.id}>
                  {lessonItem.lessonName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <div className="text-center py-4 mt-3">
            <Button
              appearance="primary"
              htmlType="submit"
              isLoading={showLoading}
              disabled={!canUpdateLesson}
            >
              Save Changes
            </Button>
          </div>
        </MDBCol>
      </MDBRow>
    </Form>
  );
};

export default EditLessonModal;
